import { Controller } from "@hotwired/stimulus"

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"

export default class extends Controller {
  static targets = ["modal", "container", "token", "error"]
  static values = { sitekey: String }

  connect() {
    this.handleEscape = this.handleEscape.bind(this)
    this.widgetId = null
    this.loadScript()
  }

  // Only the sign-in layout puts api.js in <head>. Every other page that renders
  // this form (the guest exam's register wall) loads
  // it here, as soon as the form is on the page — without it, verify() waits out
  // its 5 s and the email sign-up fails every time.
  loadScript() {
    if (window.turnstile || document.querySelector(`script[src^="${SCRIPT_SRC.split("?")[0]}"]`)) return

    const script = document.createElement("script")
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    document.head.appendChild(script)
  }

  disconnect() {
    document.removeEventListener('keydown', this.handleEscape)
    if (this.widgetId !== null && window.turnstile) {
      window.turnstile.remove(this.widgetId)
    }
  }

  verify(event) {
    // If we already have a token, let the form submit
    if (this.tokenTarget.value) {
      return
    }

    // Prevent default form submission
    event.preventDefault()

    // Show modal first
    this.showModal()

    // Wait for Turnstile script to load, then render widget
    this.waitForTurnstile()
  }

  waitForTurnstile(attempts = 0) {
    const maxAttempts = 50 // 5 seconds max wait

    if (window.turnstile) {
      this.renderWidget()
      return
    }

    if (attempts >= maxAttempts) {
      this.hideModal()
      this.showError()
      return
    }

    setTimeout(() => this.waitForTurnstile(attempts + 1), 100)
  }

  showModal() {
    this.modalTarget.classList.remove('hidden')
    document.body.classList.add('overflow-hidden')
    document.addEventListener('keydown', this.handleEscape)
  }

  hideModal() {
    this.modalTarget.classList.add('hidden')
    document.body.classList.remove('overflow-hidden')
    document.removeEventListener('keydown', this.handleEscape)

    // Remove old widget if exists
    if (this.widgetId !== null && window.turnstile) {
      window.turnstile.remove(this.widgetId)
      this.widgetId = null
    }
  }

  renderWidget() {
    // Clear any previous widget
    this.containerTarget.innerHTML = ''

    try {
      this.widgetId = window.turnstile.render(this.containerTarget, {
        sitekey: this.sitekeyValue,
        callback: (token) => this.onSuccess(token),
        'error-callback': (error) => this.onError(error),
        'expired-callback': () => this.onExpired()
      })
    } catch (error) {
      this.onError(error)
    }
  }

  onSuccess(token) {
    this.tokenTarget.value = token
    this.hideModal()
    this.element.requestSubmit()
  }

  onError(error) {
    this.hideModal()
    this.showError()
  }

  onExpired() {
    if (this.widgetId !== null && window.turnstile) {
      window.turnstile.reset(this.widgetId)
    }
  }

  showError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.remove('hidden')
    }
  }

  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('hidden')
    }
  }

  handleEscape(event) {
    if (event.key === 'Escape') {
      this.hideModal()
    }
  }

  closeModal() {
    this.hideModal()
  }
}
